import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ResponsiveContainer } from "recharts";
import { useAuth, API } from "../lib/auth";
import { Eye, Users, Briefcase, FileText, TrendingUp, Search, ArrowUp, ArrowDown, Minus } from "lucide-react";
import { useI18n } from "../lib/i18n";

const RANGES = [
  { id: "1m", labelEn: "1 Month", labelFr: "1 Mois" },
  { id: "3m", labelEn: "3 Months", labelFr: "3 Mois" },
  { id: "6m", labelEn: "6 Months", labelFr: "6 Mois" },
  { id: "1y", labelEn: "1 Year", labelFr: "1 An" },
  { id: "all", labelEn: "All Time", labelFr: "Tout" },
];

export default function AdminAnalytics() {
  const { authHeader } = useAuth();
  const { lang } = useI18n();
  const [data, setData] = useState(null);
  const [range, setRange] = useState("1m");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (r) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/analytics`, { headers: authHeader, params: { range: r } });
      setData(res.data);
    } finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => { load(range); }, [range, load]);

  if (!data) return <div className="p-8 text-center text-slate-500">Loading analytics…</div>;

  const t = data.totals;
  const keywords = data.seo?.tracked_keywords || [];

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      {/* Time range selector */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-lg font-semibold text-slate-900">
          {lang === "fr" ? "Vue d'ensemble" : "Overview"}
        </h2>
        <div className="inline-flex items-center bg-white border border-slate-200 rounded-full p-1 gap-1" data-testid="admin-range-switch">
          {RANGES.map(r => (
            <button
              key={r.id}
              data-testid={`admin-range-${r.id}`}
              onClick={() => setRange(r.id)}
              disabled={loading}
              className={`text-xs px-3 py-1.5 rounded-full transition-colors ${range === r.id ? "bg-[#1A8FE3] text-white" : "text-slate-700 hover:bg-slate-50"}`}
            >
              {lang === "fr" ? r.labelFr : r.labelEn}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <KPI icon={Eye} label={lang === "fr" ? "Vues" : "Page views"} value={t.page_views} />
        <KPI icon={Users} label={lang === "fr" ? "Candidats" : "Candidates"} value={t.candidates} />
        <KPI icon={Briefcase} label={lang === "fr" ? "Recruteurs" : "Employers"} value={t.employers} />
        <KPI icon={FileText} label={lang === "fr" ? "Offres actives" : "Live jobs"} value={t.live_jobs} />
        <KPI icon={TrendingUp} label={lang === "fr" ? "Candidatures" : "Applications"} value={t.applications} />
        <KPI icon={Search} label={lang === "fr" ? "Comptes" : "Accounts"} value={t.registered_accounts} />
      </div>

      <Card className="p-5">
        <h3 className="font-display text-base font-semibold inline-flex items-center gap-2 mb-4">
          <TrendingUp className="h-4 w-4 text-[#1A8FE3]" />
          {lang === "fr" ? "Candidatures & inscriptions" : "Applications & signups"}
        </h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.daily}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 10 }} interval={"preserveStartEnd"} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="applications" stroke="#1A8FE3" strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="signups" stroke="#10b981" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Master analytical table — daily aggregated */}
      <Card className="p-5">
        <h3 className="font-display text-base font-semibold mb-4">
          {lang === "fr" ? "Tableau analytique global" : "Master analytics table"}
        </h3>
        <div className="overflow-x-auto max-h-80">
          <table className="w-full text-sm" data-testid="admin-master-table">
            <thead className="bg-slate-50 text-slate-700 uppercase text-[10px] tracking-wider sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left">{lang === "fr" ? "Date" : "Date"}</th>
                <th className="px-3 py-2 text-right">{lang === "fr" ? "Candidatures" : "Applications"}</th>
                <th className="px-3 py-2 text-right">{lang === "fr" ? "Inscriptions" : "Signups"}</th>
              </tr>
            </thead>
            <tbody>
              {data.daily.slice().reverse().map(row => (
                <tr key={row.date} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-mono text-slate-700">{row.date}</td>
                  <td className="px-3 py-2 text-right">{row.applications}</td>
                  <td className="px-3 py-2 text-right">{row.signups}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-slate-50">
              <tr className="border-t border-slate-200 font-semibold">
                <td className="px-3 py-2">{lang === "fr" ? "Total" : "Total"}</td>
                <td className="px-3 py-2 text-right">{data.daily.reduce((s, r) => s + (r.applications || 0), 0)}</td>
                <td className="px-3 py-2 text-right">{data.daily.reduce((s, r) => s + (r.signups || 0), 0)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>

      {/* SEO snapshot + tracked keywords */}
      <Card className="p-5">
        <h3 className="font-display text-base font-semibold mb-4">{lang === "fr" ? "SEO & visibilité" : "SEO & visibility"}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm mb-6">
          <div className="p-4 rounded-xl bg-slate-50">
            <div className="text-xs uppercase tracking-widest text-slate-500">{lang === "fr" ? "Pages indexées" : "Indexed pages"}</div>
            <div className="font-display text-2xl font-bold text-slate-900 mt-1">{data.seo.indexed_pages}</div>
          </div>
          <a href={data.seo.sitemap_url} target="_blank" rel="noreferrer" className="p-4 rounded-xl bg-slate-50 hover:bg-slate-100">
            <div className="text-xs uppercase tracking-widest text-slate-500">Sitemap</div>
            <div className="font-medium text-[#1A8FE3] mt-1">{data.seo.sitemap_url}</div>
          </a>
          <a href={data.seo.robots_url} target="_blank" rel="noreferrer" className="p-4 rounded-xl bg-slate-50 hover:bg-slate-100">
            <div className="text-xs uppercase tracking-widest text-slate-500">Robots</div>
            <div className="font-medium text-[#1A8FE3] mt-1">{data.seo.robots_url}</div>
          </a>
        </div>

        {keywords.length > 0 && (
          <div data-testid="admin-keyword-table">
            <h4 className="text-xs uppercase tracking-widest text-slate-500 mb-3">
              {lang === "fr" ? "Mots-clés organiques (Mauritius)" : "Top organic keywords (Mauritius)"}
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-700 uppercase text-[10px] tracking-wider">
                  <tr>
                    <th className="px-3 py-2 text-left">{lang === "fr" ? "Mot-clé" : "Keyword"}</th>
                    <th className="px-3 py-2 text-right">{lang === "fr" ? "Position" : "Rank"}</th>
                    <th className="px-3 py-2 text-center">{lang === "fr" ? "Tendance" : "Trend"}</th>
                  </tr>
                </thead>
                <tbody>
                  {keywords.map(k => (
                    <tr key={k.keyword} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-medium text-slate-800">{k.keyword}</td>
                      <td className="px-3 py-2 text-right font-mono">#{k.rank}</td>
                      <td className="px-3 py-2 text-center">
                        {k.trend === "up" && <span className="inline-flex items-center gap-1 text-emerald-700 text-xs"><ArrowUp className="h-3 w-3" />up</span>}
                        {k.trend === "down" && <span className="inline-flex items-center gap-1 text-rose-700 text-xs"><ArrowDown className="h-3 w-3" />down</span>}
                        {k.trend === "stable" && <span className="inline-flex items-center gap-1 text-slate-500 text-xs"><Minus className="h-3 w-3" />stable</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-slate-400 mt-3">
              {lang === "fr"
                ? "Données simulées — connectez DataForSEO / SerpAPI pour des données en temps réel."
                : "Simulated data — connect DataForSEO / SerpAPI for live rank tracking."}
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}

function KPI({ icon: Icon, label, value }) {
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-medium">{label}</span>
        <Icon className="h-4 w-4 text-[#1A8FE3]" />
      </div>
      <div className="font-display text-2xl font-bold text-slate-900 mt-2">{value}</div>
    </Card>
  );
}
